import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  UserPlus, Users, Mail, Phone, Check, X, Trash2,
  Copy, Link2, Plus, Loader2, MessageCircle,
  Edit2, CheckCircle, Clock, UserX,
  ChevronUp, Smartphone, Settings, ChevronRight, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getAuthHeaders, API } from "@/lib/api";

// Check if Contact Picker API is available
const hasContactPickerAPI = () => {
  return 'contacts' in navigator && 'ContactsManager' in window;
};

// Check if on mobile device
const isMobileDevice = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

// WhatsApp helper functions
const generateWhatsAppMessage = (eventTitle, shareLink) => {
  return `Hey! 🎉 Du bist zu meinem Event "${eventTitle}" eingeladen!\n\nHier findest du alle Details und kannst zusagen:\n${shareLink}\n\nIch freue mich auf dich!`;
};

const openWhatsAppWithoutPhone = (message) => {
  const encodedMessage = encodeURIComponent(message);
  const isMobile = isMobileDevice();
  const url = isMobile
    ? `whatsapp://send?text=${encodedMessage}`
    : `https://web.whatsapp.com/send?text=${encodedMessage}`;
  window.open(url, '_blank');
};

const openWhatsApp = (phone, message) => {
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  const encodedMessage = encodeURIComponent(message);
  const isMobile = isMobileDevice();
  const url = isMobile
    ? `whatsapp://send?phone=${cleanPhone}&text=${encodedMessage}`
    : `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodedMessage}`;
  window.open(url, '_blank');
};

export default function GuestManagement({
  eventId,
  guests = [],
  onGuestAdded,
  onGuestUpdated,
  onGuestDeleted,
  shareLink,
  eventTitle = "Event",
  onBack, // Function to handle back navigation (optional)
  editable = false // NEW: explicitly allow editing even if callbacks aren't provided
}) {
  const [newGuestName, setNewGuestName] = useState("");
  const [newGuestEmail, setNewGuestEmail] = useState(""); // NEW
  const [loading, setLoading] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", email: "" });
  const [expandedGuest, setExpandedGuest] = useState(null);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [selectedGuestIds, setSelectedGuestIds] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Read-only mode for guests visiting the public page
  const isReadOnly = !editable && !onGuestAdded; // Improved logic

  const [internalGuests, setInternalGuests] = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // If guests prop is empty, fetch them internally
  useEffect(() => {
    if (eventId && (!guests || guests.length === 0)) {
      fetchInternalGuests();
    }
  }, [eventId, guests]);

  const fetchInternalGuests = async () => {
    try {
      setInternalLoading(true);
      const res = await axios.get(`${API}/events/${eventId}/guests`, { headers: getAuthHeaders() });
      setInternalGuests(res.data || []);
    } catch (err) {
      console.error("Failed to fetch guests:", err);
    } finally {
      setInternalLoading(false);
    }
  };

  // Determine which guest list to use
  const effectiveGuests = (guests && guests.length > 0) ? guests : internalGuests;
  const isInternalUsage = !guests || guests.length === 0;

  const inviteMessage = generateWhatsAppMessage(eventTitle, shareLink || `${window.location.origin}/event/${eventId}`);

  // Statistics
  const confirmedGuests = effectiveGuests.filter(g => g.status === "confirmed");
  const declinedGuests = effectiveGuests.filter(g => g.status === "declined");
  const pendingGuests = effectiveGuests.filter(g => g.status === "pending" || !g.status);

  // Add a new guest
  const handleAddGuest = async (name) => {
    const guestName = typeof name === 'string' ? name : newGuestName;
    if (!guestName.trim()) {
      toast.error("Bitte gib einen Namen ein");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: guestName.trim(),
        status: "pending"
      };

      // Add email if provided and not adding via bulk list (string arg)
      if (typeof name !== 'string' && newGuestEmail.trim()) {
        payload.email = newGuestEmail.trim();
      }

      await axios.post(
        `${API}/events/${eventId}/guests`,
        payload,
        { headers: getAuthHeaders() }
      );

      if (typeof name !== 'string') {
        toast.success(`${guestName} hinzugefügt`);
        setNewGuestName("");
        setNewGuestEmail(""); // Reset email
      }
      if (isInternalUsage) fetchInternalGuests();
      onGuestAdded?.();
    } catch (error) {
      toast.error("Fehler beim Hinzufügen");
    } finally {
      if (typeof name !== 'string') setLoading(false);
    }
  };

  // Bulk add guests
  const handleBulkAdd = async (text) => {
    const names = text.split(/[\n,;]/).map(n => n.trim()).filter(n => n.length > 1);
    if (names.length === 0) {
      toast.error("Keine gültigen Namen gefunden");
      return;
    }

    setLoading(true);
    let successCount = 0;
    for (const line of names) {
      try {
        let name = line;
        let email = "";

        // Try to extract email if in format "Name <email@...>" or "Name (email@...)"
        const emailMatch = line.match(/[<(]([^\s<>()]+@[^\s<>()]+)[>)]/);
        if (emailMatch) {
          email = emailMatch[1];
          name = line.replace(emailMatch[0], "").trim();
        } else {
          // Alternative: "Name, email@..."
          const commaParts = line.split(',');
          if (commaParts.length === 2 && commaParts[1].includes('@')) {
            name = commaParts[0].trim();
            email = commaParts[1].trim();
          }
        }

        await axios.post(
          `${API}/events/${eventId}/guests`,
          { name, email: email || undefined, status: "pending" },
          { headers: getAuthHeaders() }
        );
        successCount++;
      } catch (err) { }
    }
    setLoading(false);
    toast.success(`${successCount} Gäste hinzugefügt`);
    if (isInternalUsage) fetchInternalGuests();
    onGuestAdded?.();
  };

  const handleToggleConfirmation = async (guest) => {
    if (guest.user_id && guest.status !== "pending") {
      toast.info("Dieser Gast hat über die App zu-/abgesagt");
      return;
    }
    const newStatus = guest.status === "confirmed" ? "pending" : "confirmed";
    try {
      await axios.put(
        `${API}/events/${eventId}/guests/${guest.id}`,
        { status: newStatus, manually_confirmed: newStatus === "confirmed" },
        { headers: getAuthHeaders() }
      );
      toast.success(newStatus === "confirmed" ? "Zusagestatus gesetzt" : "Zusagestatus entfernt");
      if (isInternalUsage) fetchInternalGuests();
      onGuestUpdated?.();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteGuest = async (guestId, guestName) => {
    if (!confirm(`${guestName} wirklich entfernen?`)) return;
    try {
      await axios.delete(`${API}/events/${eventId}/guests/${guestId}`, { headers: getAuthHeaders() });
      toast.success("Gast entfernt");
      if (isInternalUsage) fetchInternalGuests();
      onGuestDeleted?.();
    } catch (error) {
      toast.error("Fehler beim Entfernen");
    }
  };

  const handleSaveContact = async (guestId) => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/guests/${guestId}`,
        { name: editForm.name, phone: editForm.phone, email: editForm.email },
        { headers: getAuthHeaders() }
      );
      toast.success("Kontaktdaten gespeichert");
      setEditingGuest(null);
      if (isInternalUsage) fetchInternalGuests();
      onGuestUpdated?.();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = (guest) => {
    const link = shareLink || `${window.location.origin}/event/${eventId}`;
    const subject = encodeURIComponent(`Einladung: ${eventTitle}`);
    const body = encodeURIComponent(`Hallo ${guest.name},\n\ndu bist herzlich zu meinem Event "${eventTitle}" eingeladen!\n\nAlle Details und Zusage hier:\n${link}\n\nIch freue mich auf dich!`);
    window.open(`mailto:${guest.email}?subject=${subject}&body=${body}`, '_blank');
  };

  const handleSendWhatsApp = (guest) => {
    if (guest.phone) {
      openWhatsApp(guest.phone, inviteMessage);
    } else {
      openWhatsAppWithoutPhone(inviteMessage);
    }
  };

  const handleCopyLink = () => {
    const link = shareLink || `${window.location.origin}/event/${eventId}`;
    navigator.clipboard.writeText(link);
    toast.success("Link kopiert!");
  };

  const startEditing = (guest) => {
    setEditingGuest(guest.id);
    setEditForm({ name: guest.name || "", phone: guest.phone || "", email: guest.email || "" });
  };

  const toggleGuestSelection = (guestId) => {
    const newSelection = new Set(selectedGuestIds);
    if (newSelection.has(guestId)) {
      newSelection.delete(guestId);
    } else {
      newSelection.add(guestId);
    }
    setSelectedGuestIds(newSelection);
    if (newSelection.size > 0) setIsSelectionMode(true);
  };

  const toggleSelectAll = () => {
    if (selectedGuestIds.size === effectiveGuests.length) {
      setSelectedGuestIds(new Set());
    } else {
      setSelectedGuestIds(new Set(effectiveGuests.map(g => g.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedGuestIds.size === 0) return;
    if (!confirm(`${selectedGuestIds.size} Gäste wirklich entfernen?`)) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/api/events/${eventId}/guests/bulk-delete`,
        { guest_ids: Array.from(selectedGuestIds) },
        { headers: getAuthHeaders() }
      );
      toast.success(`${selectedGuestIds.size} Gäste entfernt`);
      setSelectedGuestIds(new Set());
      if (isInternalUsage) fetchInternalGuests();
      onGuestDeleted?.();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedGuestIds.size === 0) return;

    setLoading(true);
    try {
      await axios.post(
        `${API}/api/events/${eventId}/guests/bulk-status`,
        { guest_ids: Array.from(selectedGuestIds), status },
        { headers: getAuthHeaders() }
      );
      toast.success(`Status für ${selectedGuestIds.size} Gäste aktualisiert`);
      setSelectedGuestIds(new Set());
      if (isInternalUsage) fetchInternalGuests();
      onGuestUpdated?.();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkInvite = async () => {
    const guestsWithEmail = effectiveGuests.filter(g => selectedGuestIds.has(g.id) && g.email);
    if (guestsWithEmail.length === 0) {
      toast.error("Keine ausgewählten Gäste haben eine E-Mail-Adresse");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/events/${eventId}/guests/bulk-invite`,
        { guest_ids: guestsWithEmail.map(g => g.id) },
        { headers: getAuthHeaders() }
      );
      toast.success(`${res.data.sent_count} Einladungen versendet`);
      setSelectedGuestIds(new Set());
      if (isInternalUsage) fetchInternalGuests();
    } catch (err) {
      toast.error("Fehler beim Versenden der Einladungen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Optional Back Button */}
      {onBack && (
        <div className="flex items-center gap-4 mb-2 animate-in fade-in slide-in-from-left-4 duration-500">
          <button
            onClick={onBack}
            className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-slate-100 shadow-sm hover:bg-slate-50 transition-colors group"
          >
            <ArrowLeft className="w-6 h-6 text-slate-400 group-hover:text-slate-900 transition-colors" />
          </button>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">Zurück</h3>
        </div>
      )}

      {/* WhatsApp Magic Banner - Only for Owners/Editors */}
      {!isReadOnly && (
        <div className="bg-[#25D366]/10 border border-[#25D366]/20 rounded-3xl md:rounded-[2.5rem] p-4 md:p-8 relative overflow-hidden group">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#25D366]/10 rounded-full blur-3xl group-hover:scale-125 transition-transform duration-1000" />
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6 relative z-10">
            <div className="flex-1">
              <h3 className="text-lg md:text-xl font-black text-slate-900 flex items-center gap-2 md:gap-3 mb-2">
                <MessageCircle className="w-5 h-5 md:w-6 md:h-6 text-[#25D366] fill-[#25D366]" /> WhatsApp Magie
              </h3>
              <p className="text-xs md:text-sm font-bold text-slate-500 leading-relaxed">
                Sende den Einladungs-Link in deine WhatsApp-Gruppe.
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <Button
                onClick={() => openWhatsAppWithoutPhone(inviteMessage)}
                className="flex-1 md:flex-none bg-[#25D366] hover:bg-[#128C7E] text-white h-12 md:h-auto px-4 md:px-8 py-0 md:py-6 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[10px] md:text-[11px] shadow-lg shadow-[#25D366]/20"
              >
                Link teilen
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl border-slate-200 hover:bg-slate-50 shrink-0"
              >
                <Copy className="w-4 h-4 md:w-5 md:h-5 text-slate-400" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-6">
        {[
          { label: 'Zusagen', count: confirmedGuests.length, color: 'emerald', icon: CheckCircle },
          { label: 'Offen', count: pendingGuests.length, color: 'amber', icon: Clock },
          { label: 'Absagen', count: declinedGuests.length, color: 'rose', icon: UserX }
        ].map((stat, i) => (
          <div key={i} className="bg-white border border-slate-100 p-2 md:p-6 rounded-2xl md:rounded-[2.5rem] text-center shadow-sm flex flex-col items-center justify-center min-h-[80px] md:min-h-0">
            <div className={`w-6 h-6 md:w-10 md:h-10 mb-1 md:mb-3 rounded-lg md:rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
              <stat.icon className={`w-3.5 h-3.5 md:w-5 md:h-5 text-${stat.color}-500`} />
            </div>
            <p className="text-lg md:text-2xl font-black text-slate-900 leading-none mb-0.5 md:mb-1">{stat.count}</p>
            <p className="text-[7px] md:text-[9px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Add Guest Controls - Only for Owners/Editors */}
      {!isReadOnly && (
        <div className="bg-white border border-slate-100 rounded-3xl md:rounded-[3rem] p-4 md:p-10 shadow-sm space-y-4 md:space-y-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-1.5 h-5 md:h-6 bg-indigo-600 rounded-full" />
              <h4 className="text-base md:text-lg font-black text-slate-900 tracking-tight">Gäste hinzufügen</h4>
            </div>
            <div className="flex items-center gap-1 md:gap-2 bg-slate-50 p-1 md:p-1.5 rounded-lg md:rounded-xl border border-slate-100">
              <button
                onClick={() => setIsBulkMode(false)}
                className={`text-[9px] md:text-[10px] font-black px-3 py-1.5 md:px-4 md:py-2 rounded-md md:rounded-lg transition-all ${!isBulkMode ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}`}
              >
                Einzeln
              </button>
              <button
                onClick={() => setIsBulkMode(true)}
                className={`text-[9px] md:text-[10px] font-black px-3 py-1.5 md:px-4 md:py-2 rounded-md md:rounded-lg transition-all ${isBulkMode ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}`}
              >
                Liste
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!isBulkMode ? (
              <motion.div
                key="single"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col gap-4 md:gap-6"
              >
                <div className="space-y-3 md:space-y-5">
                  <div className="space-y-1 md:space-y-2">
                    <label className="text-[9px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Name (Pflicht)</label>
                    <div className="relative group">
                      <UserPlus className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-slate-300 group-focus-within:text-indigo-500 transition-colors" />
                      <Input
                        value={newGuestName}
                        onChange={(e) => setNewGuestName(e.target.value)}
                        placeholder="Vor- & Nachname..."
                        className="w-full bg-slate-50 border-slate-100 h-12 md:h-16 pl-10 md:pl-14 pr-4 md:pr-6 rounded-xl md:rounded-2xl font-bold text-sm md:text-base text-slate-700"
                        onKeyPress={(e) => e.key === "Enter" && handleAddGuest()}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 md:space-y-2">
                    <label className="text-[9px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">E-Mail (Empfohlen)</label>
                    <div className="relative group">
                      <Mail className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-slate-300 group-focus-within:text-indigo-500 transition-colors" />
                      <Input
                        value={newGuestEmail}
                        onChange={(e) => setNewGuestEmail(e.target.value)}
                        placeholder="beispiel@mail.de"
                        type="email"
                        className="w-full bg-slate-50 border-slate-100 h-12 md:h-16 pl-10 md:pl-14 pr-4 md:pr-6 rounded-xl md:rounded-2xl font-bold text-sm md:text-base text-slate-700"
                        onKeyPress={(e) => e.key === "Enter" && handleAddGuest()}
                      />
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handleAddGuest}
                  disabled={loading || !newGuestName.trim()}
                  className="h-12 md:h-16 px-8 bg-indigo-600 hover:bg-slate-900 text-white rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[11px] w-full"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Hinzufügen"}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="bulk"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="p-4 md:p-6 bg-slate-50 border border-slate-100 rounded-2xl md:rounded-3xl">
                  <textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder="Max Mustermann&#10;Lisa Beispiel <lisa@beispiel.de>..."
                    className="w-full min-h-[100px] md:min-h-[120px] bg-white border border-slate-100 rounded-xl md:rounded-2xl p-4 md:p-6 font-bold text-sm md:text-base text-slate-700 focus:outline-none"
                  />
                </div>
                <Button
                  onClick={() => {
                    handleBulkAdd(bulkText);
                    setBulkText("");
                    setIsBulkMode(false);
                  }}
                  disabled={loading || !bulkText.trim()}
                  className="w-full h-12 md:h-16 bg-indigo-600 hover:bg-slate-900 text-white rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[11px]"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ganze Liste importieren"}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
          {isMobileDevice() && (
            <Button
              variant="outline"
              className="w-full h-12 md:h-16 rounded-xl md:rounded-2xl border-indigo-100 bg-indigo-50/50 text-indigo-600 font-bold text-xs md:text-base"
              onClick={async () => {
                if (hasContactPickerAPI()) {
                  try {
                    const props = ['name', 'tel', 'email'];
                    const contacts = await navigator.contacts.select(props, { multiple: true });
                    if (contacts.length > 0) {
                      setLoading(true);
                      for (const contact of contacts) {
                        const name = contact.name?.[0] || 'Unbekannt';
                        try {
                          await axios.post(`${API}/events/${eventId}/guests`, { name, status: "pending" }, { headers: getAuthHeaders() });
                        } catch (err) { }
                      }
                      onGuestAdded?.();
                      setLoading(false);
                      toast.success("Kontakte importiert");
                    }
                  } catch (err) { }
                }
              }}
            >
              <Smartphone className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
              Vom Telefonbuch
            </Button>
          )}
        </div>
      )}

      {/* Guest List Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-2">
          <h4 className="text-lg md:text-xl font-black text-slate-900 flex items-center gap-3">
            <Users className="w-5 h-5 md:w-6 md:h-6 text-indigo-500" /> Gästeliste <span className="text-slate-300 font-bold ml-1">{effectiveGuests.length}</span>
            {(loading || internalLoading) && <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
          </h4>
          {!isReadOnly && effectiveGuests.length > 0 && (
            <button
              onClick={() => {
                setIsSelectionMode(!isSelectionMode);
                if (isSelectionMode) setSelectedGuestIds(new Set());
              }}
              className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl transition-all ${isSelectionMode ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
            >
              {isSelectionMode ? 'Fertig' : 'Verwalten'}
            </button>
          )}
        </div>

        {isSelectionMode && (
          <div className="flex items-center justify-between px-4 py-2 bg-indigo-50/50 rounded-2xl border border-indigo-100 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="w-5 h-5 rounded-md border-2 border-indigo-200 flex items-center justify-center bg-white"
              >
                {selectedGuestIds.size === effectiveGuests.length && <Check className="w-3.5 h-3.5 text-indigo-600" />}
              </button>
              <span className="text-xs font-bold text-indigo-900">{selectedGuestIds.size} ausgewählt</span>
            </div>
            {selectedGuestIds.size > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedGuestIds(new Set())}
                  className="text-[10px] font-bold text-indigo-400 hover:text-indigo-600"
                >
                  Abbrechen
                </Button>
              </div>
            )}
          </div>
        )}

        {effectiveGuests.length === 0 ? (
          <div className="bg-white border border-slate-100 rounded-[2rem] md:rounded-[3rem] p-10 md:p-20 text-center shadow-sm">
            <h5 className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter mb-2">Noch keine Gäste am Start</h5>
            <p className="text-sm md:text-base text-slate-400 font-bold">Nutze die WhatsApp Magie oben oder füge Namen manuell hinzu.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {effectiveGuests.map((guest) => (
              <motion.div
                key={guest.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`group bg-white rounded-2xl md:rounded-[2.5rem] border transition-all ${selectedGuestIds.has(guest.id) ? 'border-indigo-500 ring-2 ring-indigo-500/10 shadow-lg' : 'border-slate-100 shadow-sm'} p-4 md:p-6 hover:shadow-xl hover:border-indigo-100`}
              >
                <div
                  className={`flex flex-col sm:flex-row items-center sm:items-center gap-4 ${isReadOnly ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (isReadOnly && onGuestUpdated) {
                      onGuestUpdated(guest);
                    } else if (isSelectionMode) {
                      toggleGuestSelection(guest.id);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 md:gap-4 w-full">
                    {/* Selection Checkbox */}
                    {isSelectionMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGuestSelection(guest.id);
                        }}
                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all shrink-0 ${selectedGuestIds.has(guest.id) ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white border-slate-200'}`}
                      >
                        {selectedGuestIds.has(guest.id) && <Check className="w-4 h-4" />}
                      </button>
                    )}

                    <button
                      onClick={(e) => {
                        if (isReadOnly || isSelectionMode) return;
                        e.stopPropagation();
                        handleToggleConfirmation(guest);
                      }}
                      className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center transition-all shrink-0 ${guest.status === "confirmed"
                        ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200"
                        : guest.status === "declined"
                          ? "bg-rose-500 text-white shadow-lg shadow-rose-200"
                          : "bg-slate-50 text-slate-300 border border-slate-100"
                        } ${isReadOnly ? 'cursor-default' : ''}`}
                    >
                      {guest.status === "confirmed" && <Check className="w-4 h-4 md:w-6 md:h-6" />}
                      {guest.status === "declined" && <X className="w-4 h-4 md:w-6 md:h-6" />}
                      {(!guest.status || guest.status === "pending") && <Plus className="w-4 h-4 md:w-6 md:h-6" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center font-bold text-[9px] md:text-xs shrink-0">
                          {guest.name ? guest.name.substring(0, 2).toUpperCase() : "??"}
                        </div>
                        <p className="font-black text-slate-900 truncate text-sm md:text-base">{guest.name}</p>
                        {guest.user_id && <span className="text-[8px] font-black text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded uppercase border border-indigo-100 shadow-sm">App</span>}
                      </div>

                      {!isReadOnly && (guest.phone || guest.email) && (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1 text-[9px] md:text-[10px] font-bold text-slate-400">
                          {guest.phone && (
                            <span className="flex items-center gap-1 truncate max-w-full">
                              <Smartphone className="w-3 h-3 text-indigo-400 shrink-0" /> {guest.phone}
                            </span>
                          )}
                          {guest.email && (
                            <span className="flex items-center gap-1 truncate max-w-full">
                              <Mail className="w-3 h-3 text-indigo-400 shrink-0" /> {guest.email}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {!isReadOnly && (
                    <div className="flex items-center justify-end gap-2 w-full sm:w-auto mt-2 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-slate-50">
                      <button
                        onClick={(e) => { e.stopPropagation(); expandedGuest === guest.id ? setExpandedGuest(null) : setExpandedGuest(guest.id); }}
                        className={`w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center transition-all ${expandedGuest === guest.id ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                      >
                        <Settings className={`w-3.5 h-3.5 md:w-4 md:h-4 transition-transform ${expandedGuest === guest.id ? 'rotate-90' : ''}`} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSendWhatsApp(guest); }}
                        className="w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-slate-50 text-emerald-500 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all shadow-sm"
                      >
                        <MessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteGuest(guest.id, guest.name); }}
                        className="w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-slate-50 text-rose-500 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                      >
                        <Trash2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      </button>
                    </div>
                  )}
                  {isReadOnly && (
                    <div className="shrink-0 ml-auto">
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </div>

                <AnimatePresence>
                  {expandedGuest === guest.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 md:pt-6 mt-4 md:mt-6 border-t border-slate-50 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Name</label>
                            <Input
                              value={editingGuest === guest.id ? editForm.name : guest.name || ""}
                              onChange={(e) => {
                                if (editingGuest !== guest.id) startEditing(guest);
                                setEditForm({ ...editForm, name: e.target.value });
                              }}
                              placeholder="Name..."
                              className="h-10 bg-slate-50 border-none font-bold text-xs"
                              onFocus={() => editingGuest !== guest.id && startEditing(guest)}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Telefon</label>
                            <Input
                              type="tel"
                              value={editingGuest === guest.id ? editForm.phone : guest.phone || ""}
                              onChange={(e) => {
                                if (editingGuest !== guest.id) startEditing(guest);
                                setEditForm({ ...editForm, phone: e.target.value });
                              }}
                              placeholder="0151..."
                              className="h-10 bg-slate-50 border-none font-bold text-xs"
                              onFocus={() => editingGuest !== guest.id && startEditing(guest)}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">E-Mail</label>
                            <Input
                              type="email"
                              value={editingGuest === guest.id ? editForm.email : guest.email || ""}
                              onChange={(e) => {
                                if (editingGuest !== guest.id) startEditing(guest);
                                setEditForm({ ...editForm, email: e.target.value });
                              }}
                              placeholder="hallo@..."
                              className="h-10 bg-slate-50 border-none font-bold text-xs"
                              onFocus={() => editingGuest !== guest.id && startEditing(guest)}
                            />
                          </div>
                        </div>

                        {editingGuest === guest.id && (
                          <div className="flex gap-2 pt-2">
                            <Button
                              onClick={() => handleSaveContact(guest.id)}
                              disabled={loading}
                              className="flex-1 h-10 bg-indigo-600 hover:bg-slate-900 text-[10px] font-black uppercase tracking-widest text-white rounded-xl"
                            >
                              Speichern
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setEditingGuest(null)}
                              className="flex-1 h-10 text-[10px] font-black uppercase tracking-widest rounded-xl"
                            >
                              Abbrechen
                            </Button>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1 h-10 text-[9px] font-black uppercase tracking-widest gap-2"
                            onClick={() => handleSendEmail(guest)}
                          >
                            <Mail className="w-3 h-3" /> E-Mail Einladung
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      <AnimatePresence>
        {isSelectionMode && selectedGuestIds.size > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-24 left-4 right-4 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 md:p-6 shadow-2xl flex flex-wrap items-center justify-center gap-3 md:gap-6 pointer-events-auto max-w-2xl w-full">
              <div className="hidden md:block px-4 border-r border-slate-800">
                <p className="text-white font-black text-sm">{selectedGuestIds.size}</p>
                <p className="text-slate-500 font-bold text-[9px] uppercase tracking-widest">Gäste gewählt</p>
              </div>

              <div className="flex items-center gap-2 md:gap-3">
                <Button
                  onClick={handleBulkInvite}
                  disabled={loading}
                  className="bg-indigo-600 hover:bg-indigo-700 h-10 md:h-12 px-4 md:px-6 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[10px] gap-2"
                >
                  <Mail className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Einladen</span>
                </Button>

                <div className="flex items-center bg-slate-800 p-1 rounded-xl md:rounded-2xl border border-slate-700">
                  <Button
                    variant="ghost"
                    onClick={() => handleBulkStatusUpdate("confirmed")}
                    disabled={loading}
                    className="h-8 md:h-10 px-3 md:px-4 rounded-lg md:rounded-xl text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 font-bold text-[9px] md:text-[10px]"
                  >
                    Zusage
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handleBulkStatusUpdate("pending")}
                    disabled={loading}
                    className="h-8 md:h-10 px-3 md:px-4 rounded-lg md:rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white font-bold text-[9px] md:text-[10px]"
                  >
                    Offen
                  </Button>
                </div>

                <Button
                  variant="ghost"
                  onClick={handleBulkDelete}
                  disabled={loading}
                  className="h-10 md:h-12 w-10 md:w-12 p-0 rounded-xl md:rounded-2xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Box - Only for Owners/Editors */}
      {!isReadOnly && (
        <div className="text-xs text-gray-500 p-4 md:p-8 bg-slate-50 rounded-[2rem] md:rounded-[2.5rem] border border-slate-100">
          <p className="font-black text-slate-900 uppercase tracking-widest mb-4">💡 Creator Tipps</p>
          <ul className="space-y-3">
            <li className="flex gap-3"><span className="text-indigo-500 font-bold">1.</span> Am schnellsten geht es, wenn du den WhatsApp-Link oben teilst.</li>
            <li className="flex gap-3"><span className="text-indigo-500 font-bold">2.</span> Mit der "Liste" Funktion kannst du Namenslisten einfach reinkopieren.</li>
            <li className="flex gap-3"><span className="text-indigo-500 font-bold">3.</span> Gäste, die über den Link zusagen, erscheinen automatisch mit grünem Haken.</li>
          </ul>
        </div>
      )}
    </div>
  );
}
